import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { isAxiosError } from 'axios'
import { ArrowLeft, Shield, Save, History } from 'lucide-react'
import {
  Button,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import { migrateResponsiblesV1, migrateStructureToNestedGroups, type CustomerStructure, type StructureData } from './types.ts'
import { normalizeStructureData } from './normalize.ts'
import { OusTab } from './tabs/OusTab.tsx'
import { ServersTab } from './tabs/ServersTab.tsx'
import { DrivesTab } from './tabs/DrivesTab.tsx'
import { GroupsUsersTab } from './tabs/GroupsUsersTab.tsx'
import { MatrixTabWithModes } from './tabs/MatrixTabWithModes.tsx'
import { GposTab } from './tabs/GposTab.tsx'
import { DeploymentTab } from './tabs/DeploymentTab.tsx'
import { StandardsTab } from './tabs/StandardsTab.tsx'
import { AuditTab } from './tabs/AuditTab.tsx'
import { VersionHistoryModal } from './tabs/matrix/VersionHistoryModal.tsx'
import { AdExplorerTab } from './tabs/AdExplorerTab.tsx'
import { SaveStructureDialog, type SavePayload } from './SaveStructureDialog.tsx'

const LEGACY_TABS = [
  { key: 'ous' as const, label: 'OUs' },
  { key: 'servers' as const, label: 'Server & Shares' },
  { key: 'drives' as const, label: 'Laufwerke' },
  { key: 'groups' as const, label: 'Gruppen & User' },
  { key: 'matrix' as const, label: 'Zugriffsmatrix' },
  { key: 'gpos' as const, label: 'GPO-Parameter' },
  { key: 'deployment' as const, label: 'Deployment' },
  { key: 'audit' as const, label: 'Verlauf' },
]

const COMPACT_TABS = [
  { key: 'standards' as const, label: 'Standards' },
  { key: 'matrix' as const, label: 'Zugriffsmatrix' },
  { key: 'gpos' as const, label: 'GPO-Parameter' },
  { key: 'deployment' as const, label: 'Deployment' },
  { key: 'audit' as const, label: 'Verlauf' },
]

const EXPLORER_TAB = { key: 'explorer' as const, label: 'AD-Explorer (Beta)' }

type LegacyTabKey = typeof LEGACY_TABS[number]['key']
type CompactTabKey = typeof COMPACT_TABS[number]['key']
type TabKey = LegacyTabKey | CompactTabKey | typeof EXPLORER_TAB.key

const COMPACT_FLAG_KEY = 'it-structures-compact-tabs'

function readCompactFlag(): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(COMPACT_FLAG_KEY) === '1'
}

function writeCompactFlag(on: boolean) {
  if (typeof window === 'undefined') return
  if (on) window.localStorage.setItem(COMPACT_FLAG_KEY, '1')
  else window.localStorage.removeItem(COMPACT_FLAG_KEY)
}

const EMPTY_STRUCTURE: StructureData = {
  domainPrefix: '',
  domain: '',
  servers: [],
  drives: [],
  ous: [],
  folders: [],
  groups: [],
  users: [],
  permissions: [],
}

interface ApiErrorDetail {
  message: string
  status?: number
  statusText?: string
  method?: string
  url?: string
  responseBody?: string
  timestamp: string
}

function toErrorDetail(e: unknown): ApiErrorDetail {
  const timestamp = new Date().toISOString()
  if (isAxiosError(e)) {
    let bodyText = ''
    if (e.response?.data !== undefined) {
      try {
        bodyText = typeof e.response.data === 'string'
          ? e.response.data
          : JSON.stringify(e.response.data, null, 2)
      } catch {
        bodyText = String(e.response.data)
      }
    }
    return {
      message: e.message,
      status: e.response?.status,
      statusText: e.response?.statusText,
      method: e.config?.method?.toUpperCase(),
      url: e.config?.url,
      responseBody: bodyText || undefined,
      timestamp,
    }
  }
  return {
    message: e instanceof Error ? e.message : String(e),
    timestamp,
  }
}

function formatErrorDetails(err: ApiErrorDetail): string {
  return [
    `Zeitpunkt: ${err.timestamp}`,
    err.method && err.url ? `Request:   ${err.method} ${err.url}` : null,
    err.statusText ? `Status:    ${err.status ?? ''} ${err.statusText}` : null,
    err.responseBody ? `\nResponse-Body:\n${err.responseBody}` : null,
  ]
    .filter(Boolean)
    .join('\n')
}

export function CustomerStructurePage() {
  const a = useAnonymize()
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const { toast } = useToast()
  const [compactMode, setCompactMode] = useState<boolean>(() => readCompactFlag())
  const [activeTab, setActiveTab] = useState<TabKey>(() => (readCompactFlag() ? 'standards' : 'ous'))

  function toggleCompactMode() {
    const next = !compactMode
    setCompactMode(next)
    writeCompactFlag(next)
    setActiveTab(next ? 'standards' : 'ous')
  }

  const [searchParams] = useSearchParams()
  const explorerEnabled = searchParams.get('explorer') === '1'
  const tabs = [
    ...(compactMode ? COMPACT_TABS : LEGACY_TABS),
    ...(explorerEnabled ? [EXPLORER_TAB] : []),
  ]
  const [data, setData] = useState<StructureData>(EMPTY_STRUCTURE)
  const [dirty, setDirty] = useState(false)
  const [saveError, setSaveError] = useState<ApiErrorDetail | null>(null)
  const [customerName, setCustomerName] = useState<string>(
    (location.state as { customerName?: string } | null)?.customerName ?? ''
  )
  const [customerNumber, setCustomerNumber] = useState<string | undefined>(undefined)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)

  // Wenn der Nutzer direkt per URL einsteigt (ohne customerName im Router-State),
  // laden wir Name + Kundennummer live aus TANSS — NICHT aus `domainPrefix` generieren.
  const { data: customerInfo } = useQuery<{ id: string; name: string; customerNumber?: string }>({
    queryKey: ['it-customer-info', customerId],
    queryFn: () => apiClient.get(`/it-structures/customers/${customerId}/info`).then((r) => r.data),
    enabled: !!customerId,
    retry: 1,
  })

  useEffect(() => {
    if (customerInfo?.name) setCustomerName(customerInfo.name)
    if (customerInfo?.customerNumber) setCustomerNumber(customerInfo.customerNumber)
  }, [customerInfo])

  const { data: structureResponse, error: loadError } = useQuery<{ structure: CustomerStructure | null }>({
    queryKey: ['it-customer-structure', customerId],
    queryFn: () =>
      apiClient.get(`/it-structures/customers/${customerId}/structure`).then((r) => r.data),
    enabled: !!customerId,
  })
  const hasLoaded = structureResponse !== undefined

  useEffect(() => {
    if (loadError) setSaveError(toErrorDetail(loadError))
  }, [loadError])

  useEffect(() => {
    if (structureResponse?.structure) {
      const merged = { ...EMPTY_STRUCTURE, ...structureResponse.structure.structure_data }
      const normalized = normalizeStructureData(merged)
      const nested = migrateStructureToNestedGroups(normalized)
      const migrated = migrateResponsiblesV1(nested)
      setData(migrated)
      setDirty(migrated !== normalized)
    } else if (structureResponse) {
      setData(EMPTY_STRUCTURE)
      setDirty(false)
    }
  }, [structureResponse])

  const save = useMutation({
    mutationFn: (payload: SavePayload) =>
      apiClient.put(`/it-structures/customers/${customerId}/structure`, {
        structure_data: data,
        notes: payload.notes,
        customer_name: customerName || undefined,
        ticket_contact_person_id: payload.ticket_contact_person_id ?? null,
        ticket_contact_name: payload.ticket_contact_name ?? null,
        ticket_contact_email: payload.ticket_contact_email ?? null,
        approval_input: payload.approval_input ?? null,
        skip_approval: payload.skip_approval ?? false,
      }),
    onSuccess: (r) => {
      setSaveError(null)
      setDirty(false)
      setSaveDialogOpen(false)
      qc.invalidateQueries({ queryKey: ['it-customer-structure', customerId] })
      qc.invalidateQueries({ queryKey: ['it-customer-diff', customerId] })
      qc.invalidateQueries({ queryKey: ['it-customer-approvals', customerId] })
      const payload = r.data as { approval_count?: number; tanss_ticket_id?: string | null; mail_warnings?: unknown[] }
      const ticketInfo = payload.tanss_ticket_id ? ` · TANSS-Ticket #${payload.tanss_ticket_id}` : ''
      const approvalInfo = (payload.approval_count ?? 0) > 0
        ? ` · ${payload.approval_count} Freigabe${payload.approval_count === 1 ? '' : 'n'} angefragt`
        : ''
      const warnings = Array.isArray(payload.mail_warnings) ? payload.mail_warnings.length : 0
      toast({
        title: 'Gespeichert',
        description: `Neue Version angelegt${approvalInfo}${ticketInfo}.${warnings ? ` (${warnings} Mail-Warnung${warnings === 1 ? '' : 'en'})` : ''}`,
        tone: warnings ? 'warning' : 'success',
      })
    },
    onError: (e: unknown) => setSaveError(toErrorDetail(e)),
  })

  function update(patch: Partial<StructureData>) {
    setData((d) => ({ ...d, ...patch }))
    setDirty(true)
  }

  function updateBase(field: 'domainPrefix' | 'domain', value: string) {
    setData((d) => ({ ...d, [field]: field === 'domainPrefix' ? value.toUpperCase() : value }))
    setDirty(true)
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <IconButton
              icon={<ArrowLeft />}
              aria-label="Zurück zur Übersicht"
              onClick={() => navigate('/infrastruktur/strukturen')}
            />
            <Shield className="w-7 h-7 text-brand-500" />
            {customerName ? a.name(customerName) : 'Kunde'}
          </span>
        }
        description={
          customerId
            ? `IT-Struktur, Zugriffsmatrix & Deployment · TANSS-ID ${a.code(customerId)}${customerNumber ? ` · KdNr. ${a.code(customerNumber)}` : ''}`
            : 'IT-Struktur, Zugriffsmatrix & Deployment'
        }
        actions={
          <>
            <Button
              variant="secondary"
              leadingIcon={<History className="w-4 h-4" />}
              disabled={!customerId}
              title="Versions-Historie anzeigen und Stände wiederherstellen"
              onClick={() => setHistoryOpen(true)}
            >
              Historie
            </Button>
            <Button
              variant="primary"
              leadingIcon={<Save className="w-4 h-4" />}
              disabled={!dirty || save.isPending}
              loading={save.isPending}
              onClick={() => setSaveDialogOpen(true)}
            >
              {dirty ? 'Speichern' : 'Keine Änderungen'}
            </Button>
          </>
        }
      />

      {customerId && (
        <VersionHistoryModal
          open={historyOpen}
          customerId={customerId}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {customerId && (
        <SaveStructureDialog
          open={saveDialogOpen}
          onOpenChange={setSaveDialogOpen}
          customerId={customerId}
          customerName={customerName || 'Kunde'}
          data={data}
          saving={save.isPending}
          onSubmit={async (payload) => {
            await save.mutateAsync(payload)
          }}
        />
      )}

      {saveError && (
        <ErrorState
          size="sm"
          title={
            saveError.status ? `HTTP ${saveError.status} — ${saveError.message}` : saveError.message
          }
          details={formatErrorDetails(saveError)}
        />
      )}

      {!hasLoaded && !loadError && <LoadingState label="Struktur wird geladen…" />}

      {hasLoaded && (
        <>
          {!compactMode && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label="AD-Prefix"
                type="text"
                value={a.token(data.domainPrefix ?? '')}
                onChange={(e) => {
                  if (!a.enabled) updateBase('domainPrefix', e.target.value)
                }}
                readOnly={a.enabled}
                placeholder="z.B. KDK"
                className="font-mono"
              />
              <Input
                label="AD-Domain"
                type="text"
                value={a.token(data.domain ?? '')}
                onChange={(e) => {
                  if (!a.enabled) updateBase('domain', e.target.value)
                }}
                readOnly={a.enabled}
                placeholder="z.B. kunde.local"
                className="font-mono"
              />
              <div className="flex flex-col gap-1">
                <span className="text-small font-medium text-gray-700">Beispiel-Gruppe</span>
                <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-body font-mono text-gray-500">
                  {a.token(data.domainPrefix || 'PREFIX')}_AB_Beispiel
                  {data.domain ? `@${a.token(data.domain)}` : ''}
                </div>
              </div>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
            <div className="flex items-center gap-1">
              <TabsList className="flex-1">
                {tabs.map(({ key, label }) => (
                  <TabsTrigger key={key} value={key}>
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <Button
                variant="ghost"
                size="xs"
                className="mb-1"
                title={
                  compactMode
                    ? 'Zurück zur klassischen 7-Tab-Ansicht'
                    : 'Neue 4-Tab-Ansicht ausprobieren (Standards / Matrix / GPO / Deployment)'
                }
                onClick={toggleCompactMode}
              >
                {compactMode ? 'Layout: Kompakt (4)' : 'Layout: Klassisch (7)'}
              </Button>
            </div>

            <TabsContent value="standards">
              <StandardsTab data={data} update={update} />
            </TabsContent>
            <TabsContent value="ous">
              <OusTab data={data} update={update} />
            </TabsContent>
            <TabsContent value="servers">
              <ServersTab data={data} update={update} />
            </TabsContent>
            <TabsContent value="drives">
              <DrivesTab data={data} update={update} />
            </TabsContent>
            <TabsContent value="groups">
              <GroupsUsersTab data={data} update={update} />
            </TabsContent>
            <TabsContent value="matrix">
              <MatrixTabWithModes data={data} update={update} />
            </TabsContent>
            <TabsContent value="gpos">
              <GposTab data={data} update={update} />
            </TabsContent>
            <TabsContent value="deployment">
              <DeploymentTab customerId={customerId!} dirty={dirty} />
            </TabsContent>
            <TabsContent value="audit">
              <AuditTab customerId={customerId!} />
            </TabsContent>
            <TabsContent value="explorer">
              <AdExplorerTab data={data} update={update} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  )
}
